import './App.scss';
import liff from '@line/liff';
import { LiffMockPlugin } from '@line/liff-mock';
import { useEffect, useState, useRef, useCallback, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, useLocation, matchPath, Navigate } from 'react-router-dom';
import { captureEntryAttribution } from './utils/funnelAttribution';

import MainLpContainer from './components/HomeCom/MainLpContainer';
import ClinicContainer from './components/HomeCom/ClinicContainer';
import DoctorContainer from './components/HomeCom/DoctorContainer';
import MainReserveContainer from './components/ReserveCom/MainReserveContainer';
import ReserveTakeContainer from './components/ReserveCom/ReserveTakeContainer';
import ReserveConfirmContainer from './components/ReserveCom/ReserveConfirmContainer';
import DiagnoseContainer from './components/ReserveCom/DiagnoseContainer';
import ReDiagnoseContainer from './components/ReserveCom/ReDiagnoseContainer';
import UserRescheduleContainer from './components/ReserveCom/UserRescheduleContainer';
import MypageOtherContainer from './components/MypageCom/MypageOtherContainer';
import MypageReservationContainer from './components/MypageCom/MypageReservationContainer';
import MypageInfoContainer from './components/MypageCom/MypageInfoContainer';
import MypageAddressContainer from './components/MypageCom/MypageAddressContainer';
import MypagePaymentContainer from './components/MypageCom/MypagePaymentContainer';
import PaymentBillContainer from './components/MypageCom/PaymentBillContainer';
import PaymentDeliverContainer from './components/MypageCom/PaymentDeliverContainer';
import MypageHistoryContainer from './components/MypageCom/MypageHistoryContainer';
import PaymentSuccessContainer from './components/MypageCom/PaymentSuccessContainer';
import MypageMedicalResultContainer from './components/MypageCom/MypageMedicalResultContainer';
import PaymentCancelContainer from './components/MypageCom/PaymentCancelContainer';
import MedicineMainContainer from './components/MedicineCom/MedicineMainContainer';
import MedicineInspectionContainer from './components/MedicineCom/MedicineInspectionContainer';
import ChatLoadingScreen from './components/SessionCom/ChatLoadingScreen';
import ProfileContainer from './components/DoctorCom/ProfileContainer';
import ShiftCalenderContainer from './components/DoctorCom/ShiftCalenderContainer';
import PostConsultReviewPage from './components/DoctorCom/PostConsultReviewPage';
import DoctorHistoryPage from './components/DoctorCom/DoctorHistoryPage';
import DoctorOnboardingPage from './components/DoctorCom/DoctorOnboardingPage';
import DoctorOnboardingCompletePage from './components/DoctorCom/DoctorOnboardingCompletePage';
import DoctorInviteCodePage from './components/DoctorCom/DoctorInviteCodePage';
import DoctorHomePage from './components/DoctorCom/DoctorHomePage';
import { axiosInstance } from './utils/axios.js';
import Header from './components/Header';
import HistoryPage from './components/Mypage/HistoryPage';
import SettingsPage from './components/Mypage/SettingsPage';
import AddressesPage from './components/Mypage/AddressesPage';
import ChildProfilesPage from './components/Mypage/ChildProfilesPage';
import PaymentMethodsPage from './components/Mypage/PaymentMethodsPage';
import PatientDocumentsPage from './components/Mypage/PatientDocumentsPage';
import InsuranceCardsPage from './components/Mypage/InsuranceCardsPage';
import MypagePage from './components/Mypage/MypagePage';
import HealthConditionAllowanceForm from './components/DocumentForm/HealthConditionAllowanceForm';
import AlertModal from './components/common/AlertModal';
import { LineSurveyContainer } from './components/SurveyCom/LineSurveyContainer.jsx';
import { initQAMode, isQAModeEnabled } from './utils/qaMode';
import PatientWebAuthRoutes from './components/AuthCom/PatientWebAuthRoutes';
import {
  clearPatientWebSession,
  isPatientWebAuthPath,
  loadPatientWebSession,
  normalizePatientReturnTo,
  rememberPatientClinicalConsentReturnTo,
  shouldResumeLinePatientSession,
  shouldUseLiffAuthentication,
} from './utils/patientWebAuth';

// チャット導線は巨大バンドルのためルート単位で code split（初回以外の起動を軽くする）
const ChatRouteContainer = lazy(() => import('./components/SessionCom/ChatRouteContainer'));
const LazyChatRoute = ({ token }) => (
  <Suspense fallback={<ChatLoadingScreen />}>
    <ChatRouteContainer token={token} />
  </Suspense>
);

// 本番ではデバッグログを出さない（起動時の毎回実行コストを避ける）
const appDebugLog = (...args) => {
  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.log(...args);
  }
};

liff.use(new LiffMockPlugin());

const ENTRY_LP_PATHS = [
  '/',
  '/index.html',
  '/pediatrics',
  '/diabetes',
  '/hypertension',
  '/medical_diet',
  '/erectile_dysfunction',
];

const normalizePathForEntryLp = (path) => {
  const value = (path || '/').split('?')[0].replace(/\/$/, '');
  return value || '/';
};

const isEntryLpPath = (path) => ENTRY_LP_PATHS.includes(normalizePathForEntryLp(path));

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [token, setToken] = useState();
  const [isLoading, setIsLoading] = useState(true);
  const [isWebAuthMode, setIsWebAuthMode] = useState(() => (
    isPatientWebAuthPath(window.location.pathname)
  ));
  const [errorMessage, setErrorMessage] = useState(null);
  const [globalLoading, setGlobalLoading] = useState(false);
  // チャット導線（ChatMainContainer）からの読み込みシグナル。
  // グローバル(axios)ローディングと OR で結合し、単一のオーバーレイに集約する。
  // これにより「POSTのグローバルローディング」と「チャット側の送信/リフレッシュローディング」が
  // 別々のオーバーレイとして間を空けて2回出る（＝二重ローディング）のを防ぐ。
  const [chatLoading, setChatLoading] = useState(false);
  // ローディング表示のデバウンス用（チラつき防止）
  const [showLoading, setShowLoading] = useState(false);
  const showLoadingTimerRef = useRef(null);
  const hideLoadingTimerRef = useRef(null);
  // 招待コード入力が必要な場合
  const [requiresInviteCode, setRequiresInviteCode] = useState(false);
  const [lineUidForInvite, setLineUidForInvite] = useState(null);
  const [inviteCodeMode, setInviteCodeMode] = useState('register');
  const [inviteCodeMessage, setInviteCodeMessage] = useState(null);
  const [passwordLoginUrl, setPasswordLoginUrl] = useState(null);
  // LP専用モード（LIFF認証不要）
  const [isLpOnly, setIsLpOnly] = useState(false);

  // 環境変数から取得（本番環境用）
  const PATIENT_LIFF_ID = process.env.REACT_APP_LINE_LIFF_PATIENT_ID || '2006720154-obExyzel';
  const LINE_PATIENT_FRIEND_ADD_URL =
    process.env.REACT_APP_LINE_PATIENT_FRIEND_ADD_URL ||
    'https://line.me/R/ti/p/@873cpokn';
  const DOCTOR_SHARED_LIFF_ID =
    process.env.REACT_APP_LINE_LIFF_DOCTOR_SHARED_ID ||
    null;
  const DOCTOR_LEGACY_LIFF_ID =
    process.env.REACT_APP_LINE_LIFF_DOCTOR_ID ||
    '2008308368-5vy0YneO';
  const DOCTOR_LIFF_ID = DOCTOR_SHARED_LIFF_ID || DOCTOR_LEGACY_LIFF_ID;
  const liffIdFromHostPath =
    window.location.hostname === 'liff.line.me' ? window.location.pathname.split('/')[1] : null;
  
  // 流入経路アトリビューションを最初期に保存（LIFFのリダイレクトで元URLが失われる前に）
  useState(() => { captureEntryAttribution(); return null; });

  // ページ読み込み時のURLを保存（LIFFのリダイレクト前）
  const [initialUrl] = useState(() => window.location.href);
  const [initialPath] = useState(() => window.location.pathname);
  const [initialLpPath] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const liffState = params.get('liff.state');
      if (liffState) {
        const decodedState = decodeURIComponent(liffState);
        const stateUrl = new URL(decodedState, window.location.origin);
        return stateUrl.pathname;
      }
      return window.location.pathname;
    } catch (e) {
      console.warn('[App] LP path取得エラー:', e);
      return window.location.pathname;
    }
  });
  const [initialEntryState] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('entry_state')) return params.get('entry_state');

      const liffState = params.get('liff.state');
      if (liffState) {
        const decodedState = decodeURIComponent(liffState);
        const stateParams = new URLSearchParams(decodedState.split('?')[1] || '');
        if (stateParams.get('entry_state')) return stateParams.get('entry_state');
      }

      return null;
    } catch (e) {
      console.warn('[App] entry_state取得エラー:', e);
      return null;
    }
  });
  
  // 初回アクセス時のURLパラメータを保存（refパラメータ等のトラッキング用）
  const [initialRefParam] = useState(() => {
    try {
      // 1. 現在のURLから直接取得
      const params = new URLSearchParams(window.location.search);
      if (params.get('ref')) return params.get('ref');
      
      // 2. liff.stateから取得（LIFFリダイレクト後）
      const liffState = params.get('liff.state');
      if (liffState) {
        // liff.stateはURLエンコードされた元のパス＋クエリ
        const decodedState = decodeURIComponent(liffState);
        const stateParams = new URLSearchParams(decodedState.split('?')[1] || '');
        if (stateParams.get('ref')) return stateParams.get('ref');
      }
      
      // 3. ハッシュフラグメントから取得（一部のLIFF実装用）
      if (window.location.hash) {
        const hashParams = new URLSearchParams(window.location.hash.slice(1));
        if (hashParams.get('ref')) return hashParams.get('ref');
      }
      
      return null;
    } catch (e) {
      console.warn('[App] refパラメータ取得エラー:', e);
      return null;
    }
  });
  
  // アプリ起動時にlocalStorageから認証トークンを復元
  useEffect(() => {
    const authToken = loadPatientWebSession()?.access_token
      || localStorage.getItem('medichat_auth_token');
    if (authToken) {
      axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${authToken}`;
      appDebugLog('[App起動] 認証トークン復元完了');
    }
  }, []);

  // デバッグ用：環境変数をログ出力
  appDebugLog('[環境変数チェック]', {
    NODE_ENV: process.env.NODE_ENV,
    REACT_APP_API_BASE_URL: process.env.REACT_APP_API_BASE_URL,
    REACT_APP_LINE_LIFF_PATIENT_ID: process.env.REACT_APP_LINE_LIFF_PATIENT_ID,
    REACT_APP_LINE_LIFF_DOCTOR_ID: process.env.REACT_APP_LINE_LIFF_DOCTOR_ID,
    REACT_APP_ENV: process.env.REACT_APP_ENV,
    REACT_APP_LINE_MOCK_MODE: process.env.REACT_APP_LINE_MOCK_MODE,
    PATIENT_LIFF_ID,
    DOCTOR_LIFF_ID,
  });

  // ローディング表示のデバウンス処理（チラつき防止・二重ローディング防止）
  // - globalLoading（axios在中）とchatLoading（チャット送信/リフレッシュ）をORで結合し、単一表示にする
  // - 短時間で終わる処理では表示しない（表示は180ms遅延）
  // - 非表示は即時ではなくグレース期間を置く。1操作が複数のリクエスト/リフレッシュに分かれても、
  //   その合間でオーバーレイが消えて再点灯する「二重ローディング」を防ぎ、1回の表示に合体させる。
  const HIDE_GRACE_MS = 350;
  const effectiveLoading = globalLoading || chatLoading;
  useEffect(() => {
    if (effectiveLoading) {
      if (hideLoadingTimerRef.current) {
        clearTimeout(hideLoadingTimerRef.current);
        hideLoadingTimerRef.current = null;
      }
      if (!showLoading) {
        showLoadingTimerRef.current = setTimeout(() => {
          setShowLoading(true);
        }, 180);
      }
    } else {
      if (showLoadingTimerRef.current) {
        clearTimeout(showLoadingTimerRef.current);
        showLoadingTimerRef.current = null;
      }
      hideLoadingTimerRef.current = setTimeout(() => {
        setShowLoading(false);
      }, HIDE_GRACE_MS);
    }
    return () => {
      if (showLoadingTimerRef.current) {
        clearTimeout(showLoadingTimerRef.current);
        showLoadingTimerRef.current = null;
      }
      if (hideLoadingTimerRef.current) {
        clearTimeout(hideLoadingTimerRef.current);
        hideLoadingTimerRef.current = null;
      }
    };
  }, [effectiveLoading, showLoading]);

  // 認証処理
  useEffect(() => {
    // 全APIリクエストのローディングを吸い上げる
    const onGlobalLoading = (e) => {
      const cnt = e?.detail?.count ?? 0;
      setGlobalLoading(cnt > 0);
    };
    window.addEventListener('global:loading', onGlobalLoading);

    // チャット導線（ChatMainContainer）の送信/リフレッシュ中ローディングを吸い上げ、
    // グローバルローディングと同じ単一オーバーレイに集約する。
    const onChatLoading = (e) => {
      setChatLoading(!!e?.detail?.on);
    };
    window.addEventListener('medichat:chat-loading', onChatLoading);

    const onPatientAuthExpired = () => {
      if (window.location.pathname.startsWith('/doctor')) return;

      const returnTo = normalizePatientReturnTo(
        `${window.location.pathname}${window.location.search}${window.location.hash}`,
      );
      window.history.replaceState(
        {},
        document.title,
        `/medichat/login?return_to=${encodeURIComponent(returnTo)}`,
      );
      setIsAuthenticated(false);
      setIsWebAuthMode(true);
      setIsLoading(false);
    };
    window.addEventListener('medichat:patient-auth-expired', onPatientAuthExpired);

    const onPatientClinicalConsentRequired = () => {
      if (window.location.pathname.startsWith('/doctor')) return;

      const returnTo = normalizePatientReturnTo(
        `${window.location.pathname}${window.location.search}${window.location.hash}`,
      );
      window.history.replaceState(
        {},
        document.title,
        `/medichat/login?clinical_consent=required&return_to=${encodeURIComponent(returnTo)}`,
      );
      setIsAuthenticated(false);
      setIsWebAuthMode(true);
      setIsLoading(false);
    };
    window.addEventListener(
      'medichat:patient-clinical-consent-required',
      onPatientClinicalConsentRequired,
    );

    const onLineAuthExpired = () => {
      if (window.location.pathname.startsWith('/doctor')) return;

      localStorage.removeItem('medichat_auth_token');
      delete axiosInstance.defaults.headers.common.Authorization;
      const linePath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      window.location.replace(`https://liff.line.me/${PATIENT_LIFF_ID}${linePath}`);
    };
    window.addEventListener('medichat:line-auth-expired', onLineAuthExpired);

    // QAモードの初期化
    if (isQAModeEnabled()) {
      appDebugLog('[App] QAモードが有効です。ページ読み込み後に自動チェックを実行します。');
    }

    return () => {
      window.removeEventListener('global:loading', onGlobalLoading);
      window.removeEventListener('medichat:chat-loading', onChatLoading);
      window.removeEventListener('medichat:patient-auth-expired', onPatientAuthExpired);
      window.removeEventListener(
        'medichat:patient-clinical-consent-required',
        onPatientClinicalConsentRequired,
      );
      window.removeEventListener('medichat:line-auth-expired', onLineAuthExpired);
    };
  }, []);

  useEffect(() => {
    // ──── LPパスは認証不要で即表示 ────
    const currentPath = window.location.pathname;
    const isLpPath = isEntryLpPath(currentPath);
    const initialIsDoctorPath = currentPath.startsWith('/doctor');
    const webSession = loadPatientWebSession();

    if (webSession?.access_token && !initialIsDoctorPath && !isPatientWebAuthPath(currentPath)) {
      rememberPatientClinicalConsentReturnTo(
        `${window.location.pathname}${window.location.search}${window.location.hash}`,
      );
    }

    if (isPatientWebAuthPath(currentPath)) {
      setIsWebAuthMode(true);
      setIsLoading(false);
      return;
    }

    const shouldUseLiff = shouldUseLiffAuthentication({
      hostname: window.location.hostname,
      search: window.location.search,
      userAgent: window.navigator.userAgent,
    });
    // 明示的に成立しているメール認証セッションは、リンクを開いたブラウザの
    // User-Agentだけを理由に破棄しない。既存LINE患者はWebセッションを持たないため、
    // 従来どおりLIFF認証へ進む。
    if (!initialIsDoctorPath && webSession?.access_token) {
      if (webSession.clinical_email_consent_required) {
        const returnTo = normalizePatientReturnTo(
          `${window.location.pathname}${window.location.search}${window.location.hash}`,
        );
        window.history.replaceState(
          {},
          document.title,
          `/medichat/login?clinical_consent=required&return_to=${encodeURIComponent(returnTo)}`,
        );
        setIsWebAuthMode(true);
        setIsLoading(false);
        return;
      }
      setToken(webSession.access_token);
      setIsAuthenticated(true);
      setIsWebAuthMode(false);
      setIsLoading(false);
      return;
    }

    // LIFF経由、またはLINE導線（ref/entry_state）経由の場合は認証フローへ進む
    const searchParams = new URLSearchParams(window.location.search);
    const hasLiffParams =
      window.location.search.includes('liff') ||
      searchParams.has('ref') ||
      searchParams.has('entry_state');

    if (isLpPath && !hasLiffParams && process.env.NODE_ENV !== 'development') {
      // 直接ブラウザアクセスでのLP表示（LIFF認証不要）
      appDebugLog('[App] LPパスに直接アクセス → 認証スキップ、LP表示');
      setIsLpOnly(true);
      setIsLoading(false);
      return;
    }

    if (!initialIsDoctorPath && !shouldUseLiff && process.env.NODE_ENV !== 'development') {
      const legacyLineToken = localStorage.getItem('medichat_auth_token');
      if (shouldResumeLinePatientSession({
        lineToken: legacyLineToken,
        webAccessToken: webSession?.access_token,
        pathname: window.location.pathname,
      })) {
        const linePath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
        window.location.replace(`https://liff.line.me/${PATIENT_LIFF_ID}${linePath}`);
        return;
      }

      const returnTo = normalizePatientReturnTo(
        `${window.location.pathname}${window.location.search}${window.location.hash}`,
      );
      window.history.replaceState(
        {},
        document.title,
        `/medichat/login?return_to=${encodeURIComponent(returnTo)}`,
      );
      setIsWebAuthMode(true);
      setIsLoading(false);
      return;
    }

    // 開発環境ではモック認証を使用
    if (process.env.NODE_ENV === 'development') {
      const mockLiffId = initialIsDoctorPath ? DOCTOR_LIFF_ID : PATIENT_LIFF_ID;
      
      appDebugLog('[開発環境] モック認証を開始', { isDoctorPath: initialIsDoctorPath, mockLiffId });
      
      axiosInstance.post('/auth/liff', {
        access_token: 'mocked_access_token_123',
        medichat_role: initialIsDoctorPath ? 'doctor' : 'patient',
      })
      .then((response) => {
        if (response.data.success) {
          setIsAuthenticated(true);
        } else {
          setErrorMessage('開発用モック認証に失敗しました。');
        }
        setIsLoading(false);
      })
      .catch((err) => {
        setErrorMessage(`開発用モック認証エラー: ${err.message}`);
        setIsLoading(false);
      });
      return; // 本番環境のLIFF初期化をスキップ
    }

    // --- 本番環境のLIFF認証 ---
    
    const params = new URLSearchParams(window.location.search);
    const liffState = params.get('liff.state');

    // パスベースのLIFF URL（liff.line.me/{liffId}/{path}）では liff.state が付与されないため、
    // window.location.pathname からLIFF IDプレフィックスを除去して実際のアプリパスを取得する。
    // 例: /2009163757-VSQu8acy/doctor/review → /doctor/review
    let path;
    if (liffState) {
      path = liffState;
    } else if (window.location.hostname === 'liff.line.me' && liffIdFromHostPath) {
      const segments = window.location.pathname.split('/').filter(Boolean);
      path = segments.length > 1 ? '/' + segments.slice(1).join('/') : '/';
    } else {
      path = window.location.pathname;
    }

    appDebugLog('[パス判定]', { liffState, liffIdFromHostPath, rawPathname: window.location.pathname, path });

    const isDoctorPath = path.startsWith('/doctor');
    const fallbackLiffId = isDoctorPath ? DOCTOR_LIFF_ID : PATIENT_LIFF_ID;
    // liff.line.me/{liffId}/... ではURLのLIFF IDのみで初期化する。
    // 旧LIFF IDへのフォールバックがあると、新LIFF URLでもlegacy認証になり招待コードをバイパスする。
    const doctorLiffCandidates =
      window.location.hostname === 'liff.line.me' && liffIdFromHostPath
        ? [liffIdFromHostPath]
        : [DOCTOR_SHARED_LIFF_ID, DOCTOR_LEGACY_LIFF_ID]
            .filter(Boolean)
            .filter((id, index, arr) => arr.indexOf(id) === index);
    const liffCandidates = isDoctorPath
      ? doctorLiffCandidates
      : [liffIdFromHostPath || fallbackLiffId];

    appDebugLog('[LIFF ID候補]', { liffCandidates, fallbackLiffId, liffIdFromHostPath, isDoctorPath });

    const initializeLiff = async () => {
      let lastError = null;

      for (const candidate of liffCandidates) {
        try {
          await liff.init({ liffId: candidate });
          appDebugLog('[LIFF] 初期化成功', { liffId: candidate });
          return candidate;
        } catch (error) {
          lastError = error;
          console.warn('[LIFF] 初期化失敗、次の候補を試行します', {
            liffId: candidate,
            error: error?.message || error,
          });
        }
      }

      throw lastError || new Error('LIFF initialization failed');
    };

    initializeLiff()
      .then(async (resolvedLiffId) => {

        // LINE内ブラウザかチェック → LINE外ならLINEアプリで開き直す
        if (!liff.isInClient() && window.location.hostname !== 'liff.line.me') {
          appDebugLog('[App] LINE外からのアクセス検出');
          
          const currentPath = window.location.pathname + window.location.search;
          
          // 静的ファイル（apple-pay.html等）へのアクセスはリダイレクトしない
          if (currentPath.includes('.html')) {
            appDebugLog('[App] 静的ファイルへのアクセス、リダイレクトスキップ');
            return;
          }
          
          // ルートパス（/）の場合はマーケティングLPなのでリダイレクトしない
          if (currentPath === '/' || currentPath === '') {
            appDebugLog('[App] ルートパスへのアクセス、リダイレクトスキップ');
            return;
          }

          if (!isDoctorPath) {
            const patientSession = loadPatientWebSession();
            if (patientSession?.access_token) {
              setToken(patientSession.access_token);
              setIsAuthenticated(true);
              setIsWebAuthMode(false);
              setIsLoading(false);
              return;
            }

            if (shouldResumeLinePatientSession({
              lineToken: localStorage.getItem('medichat_auth_token'),
              webAccessToken: patientSession?.access_token,
              pathname: window.location.pathname,
            })) {
              const liffUrl = `https://liff.line.me/${resolvedLiffId}${currentPath}`;
              window.location.replace(liffUrl);
              return;
            }

            const returnTo = normalizePatientReturnTo(currentPath);
            window.history.replaceState(
              {},
              document.title,
              `/medichat/login?return_to=${encodeURIComponent(returnTo)}`,
            );
            setIsWebAuthMode(true);
            setIsLoading(false);
            return;
          }

          // 医師画面は従来どおりLINEアプリで開き直す。
          const liffUrl = `https://liff.line.me/${resolvedLiffId}${currentPath}`;
          appDebugLog('[App] LINEアプリで開き直します:', liffUrl);
          window.location.replace(liffUrl);
          return;
        }

        if (!liff.isLoggedIn()) {
          appDebugLog('[LIFF] ログインしていないためリダイレクト');
          liff.login();
          return;
        }

        const accessToken = liff.getIDToken();
        // 招待コード再認証用にアクセストークンをグローバルに保存
        window.liffAccessToken = accessToken;
        window.liffResolvedId = resolvedLiffId;

        const pendingRefParam = localStorage.getItem('medichat_pending_ref_param');
        const pendingEntryState = localStorage.getItem('medichat_pending_entry_state');
        const hasLineEntryContext = !isDoctorPath && Boolean(initialRefParam || initialEntryState || pendingRefParam || pendingEntryState);

        let friendFlag = null;
        const saveEntryIntent = async () => {
          for (let attempt = 1; attempt <= 3; attempt += 1) {
            try {
              await axiosInstance.post('/auth/liff', {
                access_token: accessToken,
                medichat_role: 'patient',
                line_flag: 'true',
                entry_intent_only: true,
                ref_param: initialRefParam,
                lp_path: initialLpPath,
                entry_state: initialEntryState,
              }, {
                timeout: 30000,
              });
              return true;
            } catch (intentError) {
              console.warn(`[LIFF] LINE登録導線の事前保存に失敗 (${attempt}/3):`, intentError);
              if (attempt === 3) return false;
              await new Promise((resolve) => setTimeout(resolve, attempt * 500));
            }
          }
          return false;
        };

        // 患者用LIFFで、登録導線情報がある場合だけ友だち追加状態をチェック
        if (hasLineEntryContext) {
          try {
            const friendship = await liff.getFriendship();
            friendFlag = friendship.friendFlag === true;
            appDebugLog('[LIFF] 友だち追加状態:', friendship);
            
            if (!friendFlag) {
              // 友だち追加していない場合、まずパラメータを記録してから友だち追加URLにリダイレクト
              appDebugLog('[LIFF] 友だち未追加 → 友だち追加URLへリダイレクト');
              
              // 先にパラメータを記録（認証前だがLINE UIDはまだ取得できないので、ローカルに保存）
              if (initialRefParam) localStorage.setItem('medichat_pending_ref_param', initialRefParam);
              if (initialLpPath) localStorage.setItem('medichat_pending_lp_path', initialLpPath);
              if (initialEntryState) localStorage.setItem('medichat_pending_entry_state', initialEntryState);

              const saved = await saveEntryIntent();
              if (!saved) {
                setErrorMessage('LINE登録導線の保存に失敗しました。通信状況を確認して、もう一度お試しください。');
                setIsLoading(false);
                return;
              }
              
              // 友だち追加URLにリダイレクト
              window.location.href = LINE_PATIENT_FRIEND_ADD_URL;
              return;
            }
          } catch (e) {
            console.warn('[LIFF] getFriendship エラー（続行）:', e);
            // エラーの場合は続行（友だち追加チェックをスキップ）
          }
        }

        // バックエンドに認証リクエスト（refパラメータがあれば送信）
        const authPayload = {
          access_token: accessToken,
          medichat_role: isDoctorPath ? 'doctor' : 'patient',
          line_flag: 'true',
          liff_id: resolvedLiffId,
        };
        if (friendFlag === true) {
          authPayload.friend_flag = true;
        }
        
        // トラッキング用のrefパラメータを送信（URLから or ローカルストレージから）
        const refToSend = hasLineEntryContext ? (initialRefParam || pendingRefParam) : null;
        if (refToSend) {
          authPayload.ref_param = refToSend;
          appDebugLog('[LIFF] refパラメータ送信:', refToSend);
          // 使用後はローカルストレージから削除
          localStorage.removeItem('medichat_pending_ref_param');
        }
        const lpPathToSend = hasLineEntryContext
          ? (localStorage.getItem('medichat_pending_lp_path') || initialLpPath)
          : null;
        if (lpPathToSend) {
          authPayload.lp_path = lpPathToSend;
          appDebugLog('[LIFF] lp_path送信:', lpPathToSend);
          localStorage.removeItem('medichat_pending_lp_path');
        }
        const entryStateToSend = hasLineEntryContext ? (pendingEntryState || initialEntryState) : null;
        if (entryStateToSend) {
          authPayload.entry_state = entryStateToSend;
          appDebugLog('[LIFF] entry_state送信:', entryStateToSend);
          localStorage.removeItem('medichat_pending_entry_state');
        }
        
        axiosInstance.post('/auth/liff', authPayload, {
          timeout: 30000,
        })
        .then((response) => {
          appDebugLog('[LIFF] 認証レスポンス:', response.data);
          if (response.data.success) {
            if (response.data.auth_token) {
              clearPatientWebSession();
              localStorage.setItem('medichat_auth_token', response.data.auth_token);
              axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${response.data.auth_token}`;
              appDebugLog('[LIFF] 認証トークン保存完了');
            }
            setIsAuthenticated(true);
          } else {
            console.error('[LIFF] 認証失敗:', response.data);
            setErrorMessage(`バックエンド認証失敗: ${response.data.error || '不明なエラー'}`);
          }
          setIsLoading(false);
        })
        .catch((error) => {
          console.error('[LIFF] 認証エラー:', error);
          
          // 既に新LINE移行済みのアカウントが旧LIFFからアクセスした場合
          if (isDoctorPath && error.response?.data?.error_code === 'ALREADY_MIGRATED') {
            const newLiffUrl = error.response.data.new_liff_url;
            if (newLiffUrl) {
              appDebugLog('[LIFF] 移行済みアカウント → 新LIFFへ誘導:', newLiffUrl);
              window.location.assign(newLiffUrl);
            } else {
              setErrorMessage('このアカウントは既に新しい業務用LINEに移行済みです。新しいLINEからアクセスしてください。');
              setIsLoading(false);
            }
            return;
          }

          if (isDoctorPath && ['INVITE_CODE_REQUIRED', 'LINE_MIGRATION_REQUIRED'].includes(error.response?.data?.error_code)) {
            appDebugLog('[LIFF] 招待コード/連携必須 → アカウント連携画面へ');
            setRequiresInviteCode(true);
            setLineUidForInvite(error.response.data.line_uid);
            setInviteCodeMode(error.response.data.error_code === 'LINE_MIGRATION_REQUIRED' ? 'line-link' : 'register');
            setInviteCodeMessage(error.response.data.message || null);
            setPasswordLoginUrl(error.response.data.login_url || null);
            setIsLoading(false);
            return;
          }
          
          const errorDetail = error.response
            ? `${error.message} (Status: ${error.response.status})`
            : error.message || 'Network Error';
          setErrorMessage(`認証エラー: ${errorDetail}`);
          setIsLoading(false);
        });
      })
      .catch((error) => {
        console.error('[LIFF] 初期化エラー:', error);
        
        const errorMsg = error?.message || error?.toString() || '';
        const isChunkError = errorMsg.includes('Loading chunk') || 
                             errorMsg.includes('ChunkLoadError') ||
                             errorMsg.includes('Loading CSS chunk') ||
                             errorMsg.includes('Failed to fetch dynamically imported');
        
        const reloadKey = 'medichat_liff_reload';
        const lastReload = sessionStorage.getItem(reloadKey);
        const canReload = !lastReload || (Date.now() - Number(lastReload)) > 15000;
        
        if (isChunkError && canReload) {
          appDebugLog('[App] チャンクロードエラー検出 → ページをリロードします');
          sessionStorage.setItem(reloadKey, String(Date.now()));
          window.location.reload();
          return;
        }
        
        // LIFF固有のエラー（INIT_FAILED等）でもリロードを1回試みる
        // デプロイ直後にキャッシュされた古いHTMLが原因の場合、リロードで解決する
        if (canReload && !errorMsg.includes('INVALID_ARGUMENT')) {
          appDebugLog('[App] LIFF初期化失敗 → リロードを試みます:', errorMsg);
          sessionStorage.setItem(reloadKey, String(Date.now()));
          window.location.reload();
          return;
        }
        
        // リロール済みでも解決しない場合はエラー表示（ルートへのリダイレクトはしない）
        appDebugLog('[App] LIFF初期化失敗（リロード済み）:', errorMsg);
        setErrorMessage('LINEアプリの初期化に失敗しました。LINEアプリを再起動してお試しください。');
        setIsLoading(false);
      });
  }, []);

  // 招待コード入力成功時のコールバック
  const handleInviteCodeSuccess = (responseData) => {
    appDebugLog('[App] 招待コード認証成功:', responseData);
    if (responseData.auth_token) {
      clearPatientWebSession();
      localStorage.setItem('medichat_auth_token', responseData.auth_token);
      axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${responseData.auth_token}`;
    }
    setRequiresInviteCode(false);
    setIsAuthenticated(true);
  };

  // 招待コード入力画面でリトライ時
  const handleInviteCodeRetry = () => {
    window.location.reload();
  };

  const handlePatientWebAuthenticated = (returnTo) => {
    window.location.replace(normalizePatientReturnTo(returnTo));
  };

  // 招待コード入力画面表示
  if (requiresInviteCode) {
    return (
      <DoctorInviteCodePage 
        lineUid={lineUidForInvite}
        mode={inviteCodeMode}
        message={inviteCodeMessage}
        loginUrl={passwordLoginUrl}
        onSuccess={handleInviteCodeSuccess}
        onRetry={handleInviteCodeRetry}
      />
    );
  }

  if (isWebAuthMode) {
    return (
      <BrowserRouter>
        <PatientWebAuthRoutes onAuthenticated={handlePatientWebAuthenticated} />
      </BrowserRouter>
    );
  }

  // LP専用モード（LIFF認証不要 - 直接ブラウザアクセス時）
  if (isLpOnly) {
    return (
      <BrowserRouter>
        <div className="App">
          <MainLpContainer />
        </div>
      </BrowserRouter>
    );
  }

  // エラー表示
  if (errorMessage) {
    return (
      <div className="mc-error-screen">
        <h2 className="mc-error-title">エラーが発生しました</h2>
        <p>{errorMessage}</p>
        <pre className="mc-error-pre">
          環境変数:
          {JSON.stringify({
            NODE_ENV: process.env.NODE_ENV,
            REACT_APP_API_BASE_URL: process.env.REACT_APP_API_BASE_URL,
            REACT_APP_LINE_LIFF_PATIENT_ID: process.env.REACT_APP_LINE_LIFF_PATIENT_ID,
            REACT_APP_LINE_LIFF_DOCTOR_ID: process.env.REACT_APP_LINE_LIFF_DOCTOR_ID,
          }, null, 2)}
        </pre>
        <button onClick={() => window.location.reload()}>リロード</button>
      </div>
    );
  }

  if (isLoading) {
    return <ChatLoadingScreen />;
  }

  if (!isAuthenticated) {
    // 認証されていないが、ローディングが終わった場合はエラーを表示
    if (!isLoading && !errorMessage) {
      return (
        <div className="mc-error-screen">
          <h2 className="mc-error-title">認証に失敗しました</h2>
          <p>再度お試しいただくか、管理者にお問い合わせください。</p>
          <button onClick={() => window.location.reload()}>リロード</button>
        </div>
      );
    }
    return null;
  }
  
  // LIFF配下でのルータ basename 設定（/LIFF_ID を除去してルーティング）
  const liffIdForBasename = liffIdFromHostPath;
  const routerBasename = liffIdForBasename ? `/${liffIdForBasename}` : undefined;
  // showLoadingを使用してデバウンスされたローディング表示を行う
  const shouldBlockUi = showLoading === true;
  const softLoadingOverlay = shouldBlockUi ? <ChatLoadingScreen variant="overlay" /> : null;

  return (
    <BrowserRouter basename={routerBasename}>
      <div className="mc-app-shell">
        <AppShell token={token} />
      </div>
      {softLoadingOverlay}
    </BrowserRouter>
  );
}

// Router の“内側”で useLocation を使うコンポーネント
function AppShell({ token }) {
  const location = useLocation();
  const [globalErrorModal, setGlobalErrorModal] = useState({
    open: false,
    title: '操作に失敗しました',
    message: '',
    iconType: 'error',
  });

  // ---- 医師サポートウィジェット（全ドクターページで共通管理） ----
  const isDoctorPath =
    location.pathname.startsWith('/doctor') &&
    !location.pathname.startsWith('/doctor/setup');
  const buildTimeEmodeskWidgetUrl = process.env.REACT_APP_EMODESK_WIDGET_URL || '';
  const buildTimeEmodeskWidgetToken = process.env.REACT_APP_EMODESK_WIDGET_TOKEN || '';
  const [emodeskWidgetUrl, setEmodeskWidgetUrl] = useState(buildTimeEmodeskWidgetUrl);
  const [emodeskWidgetToken, setEmodeskWidgetToken] = useState(buildTimeEmodeskWidgetToken);
  const [widgetDoctorId, setWidgetDoctorId] = useState(null);
  // 設定が揃わずウィジェットを注入できない場合のフォールバック表示用
  const [widgetUnavailableReason, setWidgetUnavailableReason] = useState(null);

  // window グローバルに診断情報を公開（ユーザーがブラウザのコンソールから確認できるようにする）
  // 例: window.__medichatEmodeskDiag を見れば、なぜウィジェットが表示されないかが分かる
  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.__medichatEmodeskDiag = {
      isDoctorPath,
      widgetDoctorId,
      hasUrl: Boolean(emodeskWidgetUrl),
      hasToken: Boolean(emodeskWidgetToken),
      buildTimeUrlSet: Boolean(buildTimeEmodeskWidgetUrl),
      buildTimeTokenSet: Boolean(buildTimeEmodeskWidgetToken),
      runtimeGlobalConfig: window.__EMODESK_WIDGET_CONFIG__ || null,
      reason: widgetUnavailableReason,
      // LIFF 専用の切り分け用（PC doctor-frontend とは注入経路が異なる）
      isLiffInClient: typeof liff !== 'undefined' && liff.isInClient?.() === true,
      emodeskBtnPresent: Boolean(document.getElementById('emodesk-btn')),
      emodeskScriptPresent: Boolean(
        document.querySelector('script[data-emodesk-doctor-widget="true"]')
      ),
    };
  }, [
    isDoctorPath,
    widgetDoctorId,
    emodeskWidgetUrl,
    emodeskWidgetToken,
    buildTimeEmodeskWidgetUrl,
    buildTimeEmodeskWidgetToken,
    widgetUnavailableReason,
  ]);

  // ドクターパスに入ったら医師IDを取得し、離れたらリセット
  useEffect(() => {
    if (!isDoctorPath) {
      setWidgetDoctorId(null);
      setWidgetUnavailableReason(null);
      return;
    }
    let cancelled = false;
    axiosInstance
      .get('/api/medichat_doctor/profile')
      .then(({ data }) => {
        if (cancelled) return;

        const runtimeConfig = data?.emodesk_widget || {};
        const globalConfig =
          (typeof window !== 'undefined' && window.__EMODESK_WIDGET_CONFIG__) || {};
        const resolvedUrl =
          runtimeConfig.url || buildTimeEmodeskWidgetUrl || globalConfig.url || '';
        const resolvedToken =
          runtimeConfig.token || buildTimeEmodeskWidgetToken || globalConfig.token || '';

        setWidgetDoctorId(data?.doctor?.id ?? null);
        setEmodeskWidgetUrl(resolvedUrl);
        setEmodeskWidgetToken(resolvedToken);

        if (!data?.doctor?.id) {
          setWidgetUnavailableReason('doctor_id_missing');
        } else if (!resolvedUrl || !resolvedToken) {
          setWidgetUnavailableReason('widget_config_missing');
          // 本番でこの状態は EMODESK_WIDGET_URL / EMODESK_WIDGET_TOKEN（または NEXT_PUBLIC_*）
          // のサーバ環境変数が未設定。Render dashboard で設定すれば API が emodesk_widget を返す
          if (typeof console !== 'undefined') {
            // eslint-disable-next-line no-console
            console.warn(
              '[Medichat] 医師サポートチャットウィジェット未設定: ' +
                'EMODESK_WIDGET_URL / EMODESK_WIDGET_TOKEN が未設定の可能性があります。' +
                ' window.__medichatEmodeskDiag で診断情報を確認できます。'
            );
          }
        } else {
          setWidgetUnavailableReason(null);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setWidgetDoctorId(null);
        setWidgetUnavailableReason('profile_api_failed');
        if (typeof console !== 'undefined') {
          // eslint-disable-next-line no-console
          console.warn('[Medichat] /api/medichat_doctor/profile 取得失敗', err);
        }
      });
    return () => { cancelled = true; };
  }, [isDoctorPath, buildTimeEmodeskWidgetUrl, buildTimeEmodeskWidgetToken]);

  // 医師IDが確定したらウィジェットスクリプトをDOMに追加
  // widget.js が LIFF 環境などで誤って自壊（teardown）した場合も再注入する
  useEffect(() => {
    if (!widgetDoctorId || !emodeskWidgetUrl || !emodeskWidgetToken) return;

    const normalizedSrc = /\/widget\.js(\?.*)?$/.test(emodeskWidgetUrl)
      ? emodeskWidgetUrl
      : `${emodeskWidgetUrl.replace(/\/$/, '')}/widget.js`;
    const widgetKey = `medichat_${widgetDoctorId}`;

    // === Emodesk モバイルシート: オーバーレイ管理 ===
    let overlayBodyObserver = null;
    let overlayFrameObserver = null;

    function updateOverlayVisibility() {
      const frame = document.getElementById('emodesk-frame-wrap');
      const visible = Boolean(
        frame && window.getComputedStyle(frame).display !== 'none'
      );
      document.getElementById('emodesk-backdrop')?.classList.toggle('is-visible', visible);
      document.getElementById('emodesk-sheet-header')?.classList.toggle('is-visible', visible);
      document.getElementById('emodesk-sheet-floor')?.classList.toggle('is-visible', visible);

      // フレームが現れたらその style 変化も監視する
      if (frame && !overlayFrameObserver) {
        overlayFrameObserver = new MutationObserver(updateOverlayVisibility);
        overlayFrameObserver.observe(frame, {
          attributes: true,
          attributeFilter: ['style', 'class'],
        });
      }
      if (!frame && overlayFrameObserver) {
        overlayFrameObserver.disconnect();
        overlayFrameObserver = null;
      }
    }

    function closeEmodeskSheet() {
      const frame  = document.getElementById('emodesk-frame-wrap');
      const header = document.getElementById('emodesk-sheet-header');
      const floor  = document.getElementById('emodesk-sheet-floor');

      // スライドアウトアニメーション（3要素を同時に下へ退場）
      const SLIDE = 'transform 0.25s ease-in';
      [header, floor, frame].forEach(el => {
        if (!el) return;
        el.style.transition = SLIDE;
        el.style.transform  = 'translateY(110%)';
      });

      setTimeout(() => {
        // is-visible を先に剥がして Observer 起因の再表示を防ぐ
        ['emodesk-backdrop', 'emodesk-sheet-header', 'emodesk-sheet-floor'].forEach(id => {
          document.getElementById(id)?.classList.remove('is-visible');
        });
        // フレームを非表示にして Observer に検出させる
        if (frame) {
          frame.style.display    = 'none';
          frame.style.transition = '';
          frame.style.transform  = '';
        }
        if (header) { header.style.transition = ''; header.style.transform = ''; }
        if (floor)  { floor.style.transition  = ''; floor.style.transform  = ''; }
      }, 260);
    }

    function setupEmodeskOverlay() {
      if (document.getElementById('emodesk-backdrop')) return;

      // バックドロップ（背景暗転）
      const backdrop = document.createElement('div');
      backdrop.id = 'emodesk-backdrop';
      backdrop.addEventListener('click', closeEmodeskSheet);
      document.body.appendChild(backdrop);

      // セーフエリア床（ホームバー領域を白で塗りつぶす）
      const floor = document.createElement('div');
      floor.id = 'emodesk-sheet-floor';
      document.body.appendChild(floor);

      // シートヘッダー（ドラッグハンドル + 閉じるボタン）
      const header = document.createElement('div');
      header.id = 'emodesk-sheet-header';
      header.innerHTML =
        '<div class="emodesk-drag-bar"></div>' +
        '<button type="button" class="emodesk-close-btn" aria-label="チャットを閉じる">' +
        '<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">' +
        '<path d="M1 1L13 13M13 1L1 13" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/>' +
        '</svg>' +
        '</button>';
      header.querySelector('.emodesk-close-btn').addEventListener('click', closeEmodeskSheet);
      document.body.appendChild(header);

      // ── ドラッグ to close ──
      // ヘッダーを下にスワイプすると閉じるジェスチャー
      let dragStartY    = 0;
      let dragCurrentY  = 0;
      let dragActive    = false;

      function getDragEls() {
        return [
          document.getElementById('emodesk-frame-wrap'),
          document.getElementById('emodesk-sheet-header'),
          document.getElementById('emodesk-sheet-floor'),
        ];
      }

      header.addEventListener('touchstart', (e) => {
        dragStartY   = e.touches[0].clientY;
        dragCurrentY = dragStartY;
        dragActive   = true;
        getDragEls().forEach(el => { if (el) el.style.transition = 'none'; });
      }, { passive: true });

      header.addEventListener('touchmove', (e) => {
        if (!dragActive) return;
        dragCurrentY  = e.touches[0].clientY;
        const delta   = Math.max(0, dragCurrentY - dragStartY);
        const ratio   = Math.max(0, 1 - delta / 200); // 0.0 〜 1.0
        getDragEls().forEach(el => {
          if (el) el.style.transform = `translateY(${delta}px)`;
        });
        // ドラッグ量に応じてバックドロップを薄くする
        const bd = document.getElementById('emodesk-backdrop');
        if (bd) bd.style.opacity = String(0.5 * ratio);
      }, { passive: true });

      header.addEventListener('touchend', () => {
        if (!dragActive) return;
        dragActive       = false;
        const delta      = dragCurrentY - dragStartY;
        const bd         = document.getElementById('emodesk-backdrop');
        if (delta > 80) {
          closeEmodeskSheet();
        } else {
          // 80px 未満 → スナップバック
          const SNAP = 'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)';
          getDragEls().forEach(el => {
            if (!el) return;
            el.style.transition = SNAP;
            el.style.transform  = '';
          });
          if (bd) { bd.style.transition = 'opacity 0.2s ease'; bd.style.opacity = ''; }
          setTimeout(() => {
            getDragEls().forEach(el => { if (el) el.style.transition = ''; });
            if (bd) bd.style.transition = '';
          }, 200);
        }
      }, { passive: true });

      // body の childList を監視（フレームの追加/削除を検出）
      overlayBodyObserver = new MutationObserver(updateOverlayVisibility);
      overlayBodyObserver.observe(document.body, { childList: true, subtree: false });
      updateOverlayVisibility();
    }

    function teardownEmodeskOverlay() {
      overlayBodyObserver?.disconnect();
      overlayBodyObserver = null;
      overlayFrameObserver?.disconnect();
      overlayFrameObserver = null;
      document.getElementById('emodesk-backdrop')?.remove();
      document.getElementById('emodesk-sheet-header')?.remove();
      document.getElementById('emodesk-sheet-floor')?.remove();
    }

    // launcher ボタンが無くても iframe が残っていればウィジェットは生存中。
    // チャット開放中に launcher を隠す widget.js 実装があり、ボタン不在だけで
    // teardown すると送信中フレームが消え「突然送れない」になる。
    // ※ customer_service / pokecela の widget.js 自体は変更しない（ホスト側の回復のみ）
    function isEmodeskWidgetAlive() {
      if (document.getElementById('emodesk-btn')) return true;
      return Boolean(document.getElementById('emodesk-frame-wrap'));
    }

    function isEmodeskChatOpen() {
      const frame = document.getElementById('emodesk-frame-wrap');
      if (!frame) return false;
      const style = window.getComputedStyle(frame);
      return style.display !== 'none' && style.visibility !== 'hidden';
    }

    let lastInjectedAt = 0;
    let reinjectAttempts = 0;
    const maxReinjectAttempts = 3;

    function injectWidgetScript() {
      if (isEmodeskWidgetAlive()) {
        if (typeof window !== 'undefined') {
          window.__medichatEmodeskDiag = {
            ...(window.__medichatEmodeskDiag || {}),
            status: 'ready',
            chatOpen: isEmodeskChatOpen(),
            emodeskBtnPresent: Boolean(document.getElementById('emodesk-btn')),
            emodeskFramePresent: Boolean(document.getElementById('emodesk-frame-wrap')),
          };
        }
        return;
      }
      if (document.querySelector('script[data-emodesk-doctor-widget="true"]')) {
        // script だけ残って DOM が無い stale 状態 → 再注入前に掃除
        document.querySelector('script[data-emodesk-doctor-widget="true"]')?.remove();
        if (window.__emodeskWidgets) {
          delete window.__emodeskWidgets[widgetKey];
        }
      }
      document.body.classList.add('has-emodesk-widget');
      setupEmodeskOverlay();
      const script = document.createElement('script');
      script.src = normalizedSrc;
      // DoctorLayout（PC doctor-frontend）と同じく async なしで同期的に実行順を揃える
      script.dataset.site = 'medichat';
      script.dataset.token = emodeskWidgetToken;
      script.dataset.doctorId = String(widgetDoctorId);
      // LIFFはAPIと別origin。既存の医師認証付きクライアントで都度取得する。
      script.emodeskFetchIdentity = () => axiosInstance.get('/api/doctor/widget_identity', {
        skipGlobalLoading: true,
      }).then(({ data }) => data);
      script.dataset.emodeskDoctorWidget = 'true';
      script.onerror = () => {
        if (typeof console !== 'undefined') {
          // eslint-disable-next-line no-console
          console.error('[Medichat] emodesk widget.js のロード失敗', normalizedSrc);
        }
        setWidgetUnavailableReason('widget_script_load_failed');
      };
      document.body.appendChild(script);
      lastInjectedAt = Date.now();
    }

    function teardownWidgetDom({ force = false } = {}) {
      // チャット開放中は effect cleanup 以外で破棄しない
      if (!force && isEmodeskChatOpen()) return;

      if (window.__emodeskWidgets) {
        delete window.__emodeskWidgets[widgetKey];
      }
      document.getElementById('emodesk-btn')?.remove();
      document.getElementById('emodesk-frame-wrap')?.remove();
      document.querySelector('script[data-emodesk-doctor-widget="true"]')?.remove();
      teardownEmodeskOverlay();
      document.body.classList.remove('has-emodesk-widget');
    }

    injectWidgetScript();

    // widget.js が LIFF 環境でボタンだけ消して内部状態を残すことがある。
    // iframe が残っている間は「生きている」とみなし、再注入しない。
    const watchInterval = setInterval(() => {
      if (isEmodeskWidgetAlive()) {
        reinjectAttempts = 0;
        return;
      }
      if (Date.now() - lastInjectedAt < 10000) return;
      if (reinjectAttempts >= maxReinjectAttempts) return;

      reinjectAttempts += 1;
      teardownWidgetDom({ force: false });
      injectWidgetScript();
    }, 3000);

    return () => {
      clearInterval(watchInterval);
      // アンマウント時のみ強制破棄（医師パス離脱）
      teardownWidgetDom({ force: true });
    };
  }, [widgetDoctorId, emodeskWidgetUrl, emodeskWidgetToken]);

  // /doctor/review 系では固定フッター（bottom nav / FAB）と重なりやすいため、
  // emodesk ボタンを常に前面・フッター上に再配置する
  useEffect(() => {
    if (!isDoctorPath) return;

    const isDoctorReviewPath = /^\/doctor\/review(\/|$)/.test(location.pathname);

    const applyReviewWidgetStyle = () => {
      const btn = document.getElementById('emodesk-btn');
      const frameWrap = document.getElementById('emodesk-frame-wrap');
      if (!btn && !frameWrap) return;

      if (isDoctorReviewPath) {
        if (btn) {
          btn.style.zIndex = '10050';
          btn.style.bottom = 'calc(64px + max(12px, env(safe-area-inset-bottom)))';
          btn.style.left = '12px';
        }
        if (frameWrap) {
          frameWrap.style.zIndex = '10060';
        }
      } else {
        if (btn) {
          btn.style.zIndex = '';
          btn.style.bottom = '';
          btn.style.left = '';
        }
        if (frameWrap) {
          frameWrap.style.zIndex = '';
        }
      }
    };

    applyReviewWidgetStyle();
    const intervalId = setInterval(applyReviewWidgetStyle, 1000);
    return () => clearInterval(intervalId);
  }, [isDoctorPath, location.pathname]);


  // ヘッダーを非表示にするパス（完全一致 or パターンにも対応したい時は matchPath を使う）
  const HIDE_HEADER_PATTERNS = [
    ...ENTRY_LP_PATHS,
    '/reserve/diabetes/4',
    '/reserve/hyperlipidemia/3',
    '/reserve/gout/2',
    '/reserve/hypertension/1',
    '/medichat/chat',
    '/medichat/chat/start',
    '/doctor',
    '/doctor/home',
    '/doctor/profile',
    '/doctor/shifts',
    '/doctor/review',
    '/doctor/history',
    '/doctor/setup',
    '/doctor/setup/complete',
    '/medichat/chat/:id',
    '/medichat/chat/:id/:step',
    '/medichat/mypage/history',
    '/medichat/mypage/medications',
    '/medichat/mypage/settings',
    '/medichat/mypage/addresses',
    '/medichat/mypage/children',
    '/medichat/mypage/payments',
    '/doctor/review/:id',
    '/medichat/mypage/insurance',
    '/medichat/mypage/documents',
    '/medichat/document-form/:id',
    '/medichat/line-survey',
    '/line-survey'
    // 例：動的パスも隠したいなら以下のようにパターンを追加
    // '/reserve/:type/:id',
  ];

  const shouldHideHeader = HIDE_HEADER_PATTERNS.some((pattern) =>
    matchPath({ path: pattern, end: true }, location.pathname)
  );

  useEffect(() => {
    const onApiError = (event) => {
      if (location.pathname.startsWith('/doctor')) return;

      const message = event?.detail?.message;
      if (!message) return;

      setGlobalErrorModal({
        open: true,
        title: '操作に失敗しました',
        message,
        iconType: 'error',
      });
    };

    const onUserError = (event) => {
      if (location.pathname.startsWith('/doctor')) return;

      const message = event?.detail?.message;
      if (!message) return;

      setGlobalErrorModal({
        open: true,
        title: event?.detail?.title || '操作に失敗しました',
        message,
        iconType: event?.detail?.iconType || 'error',
      });
    };

    window.addEventListener('medichat:api-error', onApiError);
    window.addEventListener('medichat:user-error', onUserError);
    return () => {
      window.removeEventListener('medichat:api-error', onApiError);
      window.removeEventListener('medichat:user-error', onUserError);
    };
  }, [location.pathname]);

  return (
    <div className="App">
      {!shouldHideHeader && (
        <header className="fixed-header">
          <Header />
        </header>
      )}

      <Routes>
        <Route path="/" element={<MainLpContainer />} />
        <Route path="/index.html" element={<MainLpContainer />} />
        {ENTRY_LP_PATHS.filter((path) => path !== '/' && path !== '/index.html').map((path) => (
          <Route key={path} path={path} element={<MainLpContainer />} />
        ))}

        <Route path="/reserve" element={<MainReserveContainer />} />
        <Route path="/line-survey" element={<LineSurveyContainer />} />
        <Route path="/medichat/line-survey" element={<LineSurveyContainer />} />
        <Route path="/reserve/diagnose/:id" element={<DiagnoseContainer token={token} />} />
        <Route
          path="/reservations/:id/reschedule"
          element={<UserRescheduleContainer token={token} />}
        />
        <Route path="/reserve/rediagnose/:id" element={<ReDiagnoseContainer token={token} />} />
        <Route path="/reserve/confirm/:id" element={<ReserveConfirmContainer token={token} />} />
        <Route path="/reserve/:type/:id" element={<ReserveTakeContainer token={token} />} />

        <Route path="/mypage/reservation" element={<MypageReservationContainer token={token} />} />
        <Route path="/mypage/other" element={<MypageOtherContainer token={token} />} />
        <Route path="/mypage/other/image" element={<Navigate to="/medichat/mypage/insurance" replace />} />
        <Route
          path="/mypage/other/medical_result"
          element={<MypageMedicalResultContainer token={token} />}
        />
        <Route path="/mypage/other/info" element={<MypageInfoContainer token={token} />} />
        <Route path="/mypage/other/address" element={<MypageAddressContainer token={token} />} />
        <Route path="/mypage/payment" element={<MypagePaymentContainer token={token} />} />
        <Route path="/mypage/history" element={<MypageHistoryContainer token={token} />} />
        <Route path="/mypage/payment/bill/:id" element={<PaymentBillContainer token={token} />} />
        <Route
          path="/mypage/payment/deliver/:id"
          element={<PaymentDeliverContainer token={token} />}
        />
        <Route
          path="/mypage/insurance_card"
          element={<Navigate to="/medichat/mypage/insurance" replace />}
        />

        <Route path="/medicine" element={<MedicineMainContainer token={token} />} />
        <Route path="/medicine/inspection" element={<MedicineInspectionContainer token={token} />} />

        <Route path="/payment/success" element={<PaymentSuccessContainer token={token} />} />
        <Route path="/payment/cancel" element={<PaymentCancelContainer token={token} />} />

        <Route path="/medichat/chat" element={<LazyChatRoute token={token} />} />
        <Route path="/medichat/chat/start" element={<LazyChatRoute token={token} />} />
        <Route path="/medichat/chat/:sessionId" element={<LazyChatRoute token={token} />} />
        <Route path="/medichat/chat/:sessionId/:step" element={<LazyChatRoute token={token} />} />

        <Route path="/doctor" element={<DoctorHomePage />} />
        <Route path="/doctor/home" element={<DoctorHomePage />} />
        <Route path="/doctor/profile" element={<ProfileContainer token={token} />} />
        <Route path="/doctor/shifts" element={<ShiftCalenderContainer token={token} />} />
        <Route path="/doctor/review" element={<PostConsultReviewPage token={token} />} />
        <Route path="/doctor/review/:id" element={<PostConsultReviewPage />} />
        <Route path="/doctor/history" element={<DoctorHistoryPage />} />
        
        {/* 医師初回セットアップ */}
        <Route path="/doctor/setup" element={<DoctorOnboardingPage />} />
        <Route path="/doctor/setup/complete" element={<DoctorOnboardingCompletePage />} />

        <Route path="/medichat/mypage" element={<MypagePage />} />
        <Route path="/medichat/mypage/history" element={<HistoryPage />} />
        <Route path="/medichat/mypage/settings" element={<SettingsPage />} />
        <Route path="/medichat/mypage/addresses" element={<AddressesPage />} />
        {process.env.NODE_ENV === 'development' && (
          <Route path="/medichat/mypage/children" element={<ChildProfilesPage />} />
        )}
        <Route path="/medichat/mypage/payments" element={<PaymentMethodsPage />} />
        <Route path="/medichat/mypage/insurance" element={<InsuranceCardsPage />} />
        <Route path="/medichat/mypage/documents" element={<PatientDocumentsPage />} />
        
        {/* 書類フォーム（傷病手当金申請書など） */}
        <Route path="/medichat/document-form/:id" element={<HealthConditionAllowanceForm />} />
      </Routes>

      <AlertModal
        open={globalErrorModal.open}
        title={globalErrorModal.title}
        message={globalErrorModal.message}
        iconType={globalErrorModal.iconType}
        onClose={() => setGlobalErrorModal((prev) => ({ ...prev, open: false }))}
      />
    </div>
  );
}

export default App;
